import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Add } from '@mui/icons-material';
import { FormState } from 'final-form';
import { omit } from 'lodash-es';

import Loadable from '@components/Loadable/Loadable';
import Modal from '@components/Modal/Modal';
import { TInquiryStatus } from '@interfaces/inquiries';
import { IUserEnteredSupplementData } from '@interfaces/supplements';
import { fileUploadProgressSelector } from '@store/features/fileUploadState/selectors';
import { handleCreateNewSupplement } from '@store/features/inquiryDraft/createNewSupplement/actions';
import { createNewSupplementSelector } from '@store/features/inquiryDraft/createNewSupplement/selectors';
import { removeAddSupplement } from '@store/features/inquiryDraft/createNewSupplement/slice';
import { inquiryDetailsSelector } from '@store/features/inquiryDraft/inquiryDetails/selectors';
import {
  handleEditUserEnteredSupplement,
  handleGetUserEnteredSupplementDetails,
} from '@store/features/inquiryDraft/userEnteredSupplementDetails/actions';
import {
  userEnteredSupplementErrorSelector,
  userEnteredSupplementIsLoadingSelector,
  userEnteredSupplementSelector,
} from '@store/features/inquiryDraft/userEnteredSupplementDetails/selectors';
import { removeUserEnteredSupplementDetails } from '@store/features/inquiryDraft/userEnteredSupplementDetails/slice';
import useDispatch from '@store/hooks/useDispatch';
import { EventType, useEventBus } from '@utils/eventBus';
import handleToast from '@utils/handleToast';

import UserEnteredSupplementForm from './UserEnteredSupplementForm/UserEnteredSupplementForm';
import { IUserEnteredSupplementModalProps } from './UserEnteredSupplementModal.interface';

import './style.scss';

const UserEnteredSupplementModal: FC<IUserEnteredSupplementModalProps> = ({
  userEnteredSupplementId,
  open,
  onClose,
  onExited,
  onAddToInquiry,
}) => {
  const { t } = useTranslation();
  const eventBus = useEventBus('userEnteredSupplementModal');

  const dispatch = useDispatch();
  const addSupplementResponse = useSelector(createNewSupplementSelector);
  const [formState, setFormState] = useState<FormState<IUserEnteredSupplementData>>();

  const userEnteredSupplement = useSelector(userEnteredSupplementSelector);
  const userEnteredSupplementIsLoading = useSelector(userEnteredSupplementIsLoadingSelector);
  const userEnteredSupplementError = useSelector(userEnteredSupplementErrorSelector);
  const inquiryDetails = useSelector(inquiryDetailsSelector);
  const uploadingFilesInProgress = useSelector(fileUploadProgressSelector);

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const disabled = !!inquiryDetails && inquiryDetails?.inquiryStatusId !== TInquiryStatus.Draft;

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const isRightActionDisabled =
    formState?.hasValidationErrors ||
    userEnteredSupplementIsLoading ||
    disabled ||
    uploadingFilesInProgress;

  const handleFetchUserEnteredSupplement = useCallback(() => {
    userEnteredSupplementId &&
      dispatch(handleGetUserEnteredSupplementDetails({ id: userEnteredSupplementId }));
  }, [dispatch, userEnteredSupplementId]);

  useEffect(handleFetchUserEnteredSupplement, [handleFetchUserEnteredSupplement]);

  const initialValues = useMemo(
    () => (userEnteredSupplement ? omit(userEnteredSupplement, ['id']) : {}),
    [userEnteredSupplement],
  );

  const handleClose = useCallback((): void => {
    onClose();
    dispatch(removeUserEnteredSupplementDetails());
  }, [dispatch, onClose]);

  const handleValidate = useCallback((values: IUserEnteredSupplementData) => {
    const errors: Partial<Record<keyof IUserEnteredSupplementData, string | boolean>> = {};

    if (!values.supplementName) errors.supplementName = true;
    if (!values.companyName) errors.companyName = true;
    if (!values.linkToProductPage) errors.linkToProductPage = true;

    return errors;
  }, []);

  const handleSubmit = useCallback(
    (values: IUserEnteredSupplementData) => {
      if (isRightActionDisabled) {
        return;
      }
      if (userEnteredSupplementId) {
        return dispatch(
          handleEditUserEnteredSupplement({
            id: userEnteredSupplementId,
            ...values,
          }),
        );
      }
      return dispatch(handleCreateNewSupplement(values));
    },
    [dispatch, userEnteredSupplementId, isRightActionDisabled],
  );

  useEffect(() => {
    addSupplementResponse && onAddToInquiry(null, addSupplementResponse.id);
    dispatch(removeAddSupplement());
  }, [dispatch, addSupplementResponse, onAddToInquiry]);

  useEffect(() => {
    eventBus.on(EventType.CREATE_NEW_SUPPLEMENT_SUCCESS, () =>
      handleToast('success', t('addSupplementModal.supplementCreatedSuccessfully')),
    );

    eventBus.on(EventType.EDIT_NEW_SUPPLEMENT_SUCCESS, () =>
      handleToast('success', t('addSupplementModal.supplementEditedSuccessfully')),
    );

    eventBus.on(
      [
        EventType.CREATE_NEW_INQUIRY_SUCCESS,
        EventType.ADD_NEW_SUPPLEMENT_TO_INQUIRY_SUCCESS,
        EventType.EDIT_NEW_SUPPLEMENT_SUCCESS,
      ],
      handleClose,
    );

    return () => {
      eventBus.off([
        EventType.CREATE_NEW_INQUIRY_SUCCESS,
        EventType.CREATE_NEW_SUPPLEMENT_SUCCESS,
        EventType.EDIT_NEW_SUPPLEMENT_SUCCESS,
        EventType.ADD_NEW_SUPPLEMENT_TO_INQUIRY_SUCCESS,
      ]);
    };
  }, []);

  return (
    <Modal
      onClose={handleClose}
      onExited={onExited}
      open={open}
      title={
        userEnteredSupplementId
          ? t('addSupplementModal.editTitle')
          : t('addSupplementModal.createTitle')
      }
      className="user-entered-supplement-modal"
      leftActionTitle={t('common.cancel')}
      onLeftAction={handleClose}
      rightActionIcon={<Add />}
      rightActionTitle={
        userEnteredSupplementId
          ? t('addSupplementModal.saveSupplement')
          : t('addSupplementModal.addSupplement')
      }
      isRightActionLoading={formState?.submitting}
      isRightActionDisabled={isRightActionDisabled}
      submitFormId="user-entered-supplement-form"
    >
      <Loadable
        isLoading={userEnteredSupplementIsLoading}
        error={userEnteredSupplementError && t('errors.userEnteredSupplementError')}
        onRetry={handleFetchUserEnteredSupplement}
      >
        <Form initialValues={initialValues} validate={handleValidate} onSubmit={handleSubmit}>
          {props => (
            <>
              <UserEnteredSupplementForm {...props} disabled={disabled} />
              <FormSpy
                subscription={{ submitting: true, hasValidationErrors: true }}
                onChange={setFormState}
              />
            </>
          )}
        </Form>
      </Loadable>
    </Modal>
  );
};

export default UserEnteredSupplementModal;
